import { EntityState } from "@reduxjs/toolkit";
export type Nullable<T> = T | null | undefined;

export type Role =
  | "all"
  | "administrators"
  | "developers"
  | "coordinators"
  | "customerusers"
  | "dashboarduser"
  | "datawriter"
  | "datareader";

export interface CloudPlatformSelections {
  aws: boolean;
  azure: boolean;
  gcp: boolean;
}

export interface ITaskUpdate {
  customer_guid: string;
  task_guid: string;
  task_name: string;
  task_desc: Nullable<string>;
  is_activated: boolean;
  task_vms: string[];
  task_config: TaskConfig;
  cloud_platforms: CloudPlatformSelections;
}

export interface ITask {
  customer_guid: string;
  task_guid: string;
  task_name: string;
  is_activated: boolean;
  task_description: Nullable<string>;
  screenshots_allowed: Nullable<boolean>;
  recording_allowed: Nullable<boolean>;
  vm_names: string[];
  priority: Nullable<number>;
  start_script_path: Nullable<string>;
  task_config_name: string;
  task_config_guid: string;
  aws_task_configs: null;
  azure_task_configs: Nullable<AzureTaskConfig>;
  gcp_task_configs: Nullable<GoogleTaskConfig>;
  cloud_platforms: CloudPlatformSelections;
  technical_contact_email: Nullable<string>;
  business_contact_email: Nullable<string>;
  customer_email: Nullable<string>;
  customer_error_email: Nullable<string>;
  onsite_implementation: boolean;
  additional_info: Nullable<string>;
}

export interface AzureTaskConfig {
  azure_task_config_guid?: Nullable<string>;
  azure_task_config_name?: Nullable<string>;
  keyvault_prod_base_url: Nullable<string>;
  keyvault_test_base_url: Nullable<string>;
  data_storage_account: Nullable<string>;
  diagnostics_storage_account: Nullable<string>;
}

export interface GoogleTaskConfig {
  prod_project_id: Nullable<string>;
  test_project_id: Nullable<string>;
  prod_data_storage_bucket_id: Nullable<string>;
  prod_diagnostics_storage_bucket_id: Nullable<string>;
  test_data_storage_bucket_id: Nullable<string>;
  test_diagnostics_storage_bucket_id: Nullable<string>;
}

export interface ITaskCreate {
  customer_guid: string;
  task_name: string;
  task_desc: Nullable<string>;
  cloud_platforms: CloudPlatformSelections;
  is_activated?: boolean;
  task_config: TaskConfig;
}

export interface TaskConfig {
  task_config_guid?: Nullable<string>; // Optional, as it may not be known at creation
  task_config_name?: Nullable<string>;
  task_guid?: string; // Optional, as it may not be known at creation
  priority?: number;
  screenshots_allowed?: boolean;
  recording_allowed?: boolean;
  start_script_path?: string;
  azure_config?: Nullable<AzureTaskConfig>;
  gcp_config?: Nullable<GoogleTaskConfig>;
  onsite_implementation?: boolean;
  additional_configs?: object;
}

export interface ITasksState extends EntityState<ITask> {
  selectedTask: string;
  status: string;
}

// TODO: Did we decide to keep EntityState or some other way to keep track of state from old code?
export interface IScheduleState extends EntityState<ISchedule> {
  selectedSchedule: string;
  status: string;
}

export interface IScheduleFormObject {
  id: string;
  vmName: string[];
  taskName: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  recurrenceType: string;
  frequency: number;
  dayNumber: number;
  runsMonday: boolean;
  runsTuesday: boolean;
  runsWednesday: boolean;
  runsThursday: boolean;
  runsFriday: boolean;
  runsSaturday: boolean;
  runsSunday: boolean;
  cronString: string;
  duration_in_minutes: number;
}

export interface ISchedule {
  schedule_guid: string;
  customer_guid: string;
  task_guid: string;
  target_vm: Nullable<string>;
  task_name: string;
  scheduled_start_time: Date;
  scheduled_end_time: Date;
  daily_recurrence: Nullable<IDailyRecurrence>;
  weekly_recurrence: Nullable<IWeeklyRecurrence>;
  monthly_recurrence: Nullable<IMonthlyRecurrence>;
  cron_recurrence: Nullable<ICronRecurrence>;
  timezone: string;
  event_created_for_next_run: boolean;
  next_run?: Date;
}

export interface IScheduleCreate {
  customer_guid: string;
  task_guid: string;
  vm_name: Nullable<string>;
  start_time: Date;
  end_time: Date;
  daily_recurrence: Nullable<IDailyRecurrence>;
  weekly_recurrence: Nullable<IWeeklyRecurrence>;
  monthly_recurrence: Nullable<IMonthlyRecurrence>;
  cron_recurrence: Nullable<ICronRecurrence>;
  timezone: string;
}

export function mapIScheduleToIScheduleUpdate(obj: ISchedule): IScheduleUpdate {
  const result: IScheduleUpdate = {
    schedule_guid: obj.schedule_guid,
    customer_guid: obj.customer_guid,
    task_guid: obj.task_guid,
    vm_name: obj.target_vm,
    start_time: obj.scheduled_start_time,
    end_time: obj.scheduled_end_time,
    daily_recurrence: obj.daily_recurrence,
    weekly_recurrence: obj.weekly_recurrence,
    monthly_recurrence: obj.monthly_recurrence,
    cron_recurrence: obj.cron_recurrence,
    timezone: obj.timezone,
  };
  return result;
}

export interface IScheduleUpdate {
  schedule_guid: string;
  customer_guid: string;
  task_guid: string;
  vm_name: Nullable<string>;
  start_time: Date;
  end_time: Date;
  daily_recurrence: Nullable<IDailyRecurrence>;
  weekly_recurrence: Nullable<IWeeklyRecurrence>;
  monthly_recurrence: Nullable<IMonthlyRecurrence>;
  cron_recurrence: Nullable<ICronRecurrence>;
  timezone: string;
}

export interface IScheduleDelete {
  schedule_guid: string;
  customer_guid: string;
}

export interface IDailyRecurrence {
  frequency: number;
}

export interface IWeeklyRecurrence {
  frequency: number;
  runs_monday: boolean;
  runs_tuesday: boolean;
  runs_wednesday: boolean;
  runs_thursday: boolean;
  runs_friday: boolean;
  runs_saturday: boolean;
  runs_sunday: boolean;
}

export interface IMonthlyRecurrence {
  frequency: number;
  day_number: number;
}

export interface ICronRecurrence {
  cron_string: string;
  duration_in_minutes: number;
}

export interface IStamp {
  customer_guid: string;
  task_guid: string;
  stamp_name: string;
  stamp_screen_name: string;
  stamp_description: string;
}
